import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BiX, BiTrash, BiUpvote, BiDownvote } from 'react-icons/bi';
import { useLeaderboardMessage } from '../../services/hooks/useLeaderboardMessage';

/**
 * Modal for creating/editing Elden Ring style leaderboard messages
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {string} mode - 'create' or 'edit' mode
 */
const LeaderboardMessageModal = ({ isOpen, onClose, mode = 'create' }) => {
  const {
    userMessage,
    loading,
    hasMessage,
    createOrUpdateMessage,
    updateMessage,
    deleteMessage,
    canCreateMessage,
    getPublicUsername
  } = useLeaderboardMessage();

  const [messageText, setMessageText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const previousOverflow = useRef();

  const userUpvotes = userMessage?.upvotes ?? 0;
  const userDownvotes = userMessage?.downvotes ?? 0;
  const lastUpdated = userMessage?.updatedAt ? new Date(userMessage.updatedAt) : null;

  // Initialize message text when modal opens or user message changes
  useEffect(() => {
    if (isOpen) {
      setMessageText(hasMessage ? userMessage.message : '');
      setShowDeleteConfirm(false);
    }
  }, [isOpen, userMessage, hasMessage]);

  // Body scroll locking effect
  useEffect(() => {
    if (isOpen) {
      // Store current overflow style
      previousOverflow.current = document.body.style.overflow;
      
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      
      // Add padding to prevent layout shift on mobile
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        // Restore previous overflow
        document.body.style.overflow = previousOverflow.current || '';
        document.body.style.paddingRight = '';
      };
    }
  }, [isOpen]);

  // Don't render if not open
  if (!isOpen) return null;

  const publicUsername = getPublicUsername();
  const canCreate = canCreateMessage();
  const remainingChars = 30 - messageText.length;
  const isValid = messageText.trim().length > 0 && messageText.length <= 30;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValid || !canCreate) return;

    setIsSubmitting(true);
    try {
      const success = await createOrUpdateMessage(messageText.trim());
      if (success) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      const success = await deleteMessage();
      if (success) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      <div 
        className="bg-gray-900 border-2 border-gray-600 rounded-lg shadow-2xl w-80 max-w-[90vw] transform transition-all duration-200 scale-100"
        style={{
          maxHeight: '80vh'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-600">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-medium text-orange-400">
              {hasMessage ? 'Edit' : 'Create'} Message
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50"
          >
            <BiX className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {!canCreate ? (
            <div className="text-center py-6">
              <span className="text-3xl mb-3 block">⚠️</span>
              <h3 className="text-sm font-medium text-gray-200 mb-2">
                Username Required
              </h3>
              <p className="text-xs text-gray-400 mb-4">
                Set a public username in settings first
              </p>
              <button
                onClick={handleClose}
                className="bg-orange-600 hover:bg-orange-700 text-white text-sm px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Go to Settings
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {hasMessage && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                    Your message stats
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 text-green-400 font-semibold">
                        <BiUpvote className="text-base" />
                        <span>{userUpvotes}</span>
                      </div>
                      <div className="flex items-center gap-1 text-red-400 font-semibold">
                        <BiDownvote className="text-base" />
                        <span>{userDownvotes}</span>
                      </div>
                    </div>
                    {lastUpdated && (
                      <div className="text-[10px] text-right text-gray-500 leading-tight">
                        Updated {lastUpdated.toLocaleString()}
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2">
                    Saving a new message resets your votes so fresh reactions can roll in.
                  </p>
                </div>
              )}

              {/* Message Input */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Leave a message for other players
                </label>
                <div className="relative">
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Try finger but hole..."
                    maxLength={30}
                    rows={2}
                    disabled={isSubmitting}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <div className="absolute bottom-2 right-2">
                    <span className={`text-xs font-medium ${
                      remainingChars < 5 ? 'text-red-400' : 'text-gray-500'
                    }`}>
                      {remainingChars}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between gap-2 pt-2">
                {hasMessage && !showDeleteConfirm && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isSubmitting}
                    className="flex items-center gap-1 text-red-400 hover:text-red-300 transition-colors disabled:opacity-50 text-sm"
                  >
                    <BiTrash className="text-sm" />
                    <span>Delete</span>
                  </button>
                )}

                {showDeleteConfirm && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isSubmitting}
                      className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? 'Deleting...' : 'Confirm'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isSubmitting}
                      className="text-gray-400 hover:text-gray-200 text-xs disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                <div className="flex items-center gap-2 ml-auto">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 text-sm px-3 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!isValid || isSubmitting}
                    className="bg-orange-600 hover:bg-orange-700 text-white text-sm px-4 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting 
                      ? 'Saving...' 
                      : hasMessage 
                        ? 'Update' 
                        : 'Create'
                    }
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default LeaderboardMessageModal;